import {
  Directive,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  SimpleChanges,
  forwardRef,
  ElementRef,
} from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import {
  MatAutocomplete,
  MatAutocompleteTrigger,
} from '@angular/material/autocomplete';
import { BehaviorSubject, Observable, Subscription, combineLatest } from 'rxjs';
import {
  delay,
  distinctUntilChanged,
  map,
  shareReplay,
  startWith,
  take,
  tap,
} from 'rxjs/operators';
import { SelectOption, SelectOptionLowercase } from './types';

@Directive({
  exportAs: 'customAutocomplete',
  selector: '[ciaoMatAutocompleteCustomAccessor]',
  standalone: true,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => MatAutocompleteCustomAccessorDirective),
      multi: true,
    },
  ],
})
export class MatAutocompleteCustomAccessorDirective<T>
  extends MatAutocompleteTrigger
  implements OnInit, OnDestroy
{
  readonly setModelValue$ = new BehaviorSubject<T>(null);
  readonly setViewValue$ = new BehaviorSubject<string | SelectOption<T>>('');
  subscription: Subscription;

  @Input('ciaoMatAutocompleteCustomAccessor')
  override autocomplete: MatAutocomplete;

  @Input() compareWith = (a: T, b: T) => a === b;
  /** When true, form control stores the full option { value, label } so the dropdown keeps showing the label. When false, control stores only option.value (e.g. for state, crew). */
  @Input() storeFullOption = false;
  @Input() options$: Observable<SelectOption<T>[]>;
  filteredOptions$: Observable<SelectOption<T>[]>;

  constructor(private elementRef: ElementRef) {
    super();
  }

  ngOnInit() {
    this.reinitialize();
  }
  ngOnDestroy() {
    this.subscription.unsubscribe();
  }
  ngOnChanges(changes: SimpleChanges): void {
    if (changes.options$) {
      this.reinitialize();
    }
  }

  // constructor() { }
  override writeValue(modelValue: T) {
    this.setModelValue$.next(modelValue);
  }

  override registerOnChange(fn: (value: any) => {}): void {
    const fn2 = (viewValue: any) => {
      this.setViewValue$.next(viewValue);
      // While the user is typing, `viewValue` is the raw search string rather than
      // a selected option. Don't propagate that to the form control: doing so nulls
      // the control on every keystroke, which marks the form dirty and resets
      // dependent fields (e.g. the auto-filled supervisor, or the okay-modal crew
      // array). Only commit an actual option selection (an object).
      if (viewValue && typeof viewValue === 'object') {
        return this.storeFullOption ? fn(viewValue) : fn(viewValue.value);
      }
      return undefined;
    };
    super.registerOnChange(fn2);
  }

  displayWith(item: { value: string; label: string }) {
    return item?.label;
  }

  @HostListener('focusout') onBlurHandler() {
    // If the user types in the value and tabs off then fill the field value with the corresponding dropdown value
    // This only applys if there's only one option in the list
    this.filteredOptions$
      .pipe(
        take(1),
        // delay needed to make sure that clicking on the option doesn't break things
        delay(100),
        tap((options) => {
          if (options.length === 1) {
            this._onChange(options[0]);
          }
        })
      )
      .subscribe();
  }

  @HostListener('keydown', ['$event']) onKeyDown(event: KeyboardEvent) {
    // Handle arrow key navigation scrolling
    if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
      // Use requestAnimationFrame to allow Angular Material to update the active option first
      requestAnimationFrame(() => {
        setTimeout(() => {
          this.scrollActiveOptionIntoView();
        }, 10);
      });
    }
  }

  /**
   * Scrolls the currently active option into view within the autocomplete panel
   */
  private scrollActiveOptionIntoView(): void {
    if (!this.autocomplete || !this.panelOpen) {
      return;
    }

    try {
      // Get the panel element - MatAutocompleteTrigger has a protected panel property
      // Access it through the autocomplete's panel or the trigger's panel
      const panel = this.autocomplete.panel?.nativeElement || 
                    (this as any).panel?.nativeElement ||
                    document.querySelector('.mat-mdc-autocomplete-panel, .mat-autocomplete-panel');
      if (!panel) {
        return;
      }

      // Find the active option element - Material uses different classes for active state
      // Try multiple selectors to catch different Material versions
      const activeOption = panel.querySelector(
        '.mat-mdc-option.mdc-list-item--selected, ' +
        '.mat-mdc-option.mat-mdc-option-active, ' +
        '.mat-option.mat-active, ' +
        '.mat-option.mat-selected, ' +
        '.mdc-list-item--selected'
      ) as HTMLElement;
      
      if (!activeOption) {
        return;
      }

      // Check if virtual scrolling is being used
      const virtualScrollViewport = panel.querySelector('cdk-virtual-scroll-viewport');
      if (virtualScrollViewport) {
        // For virtual scrolling, find the index and scroll to it
        const options = Array.from(panel.querySelectorAll('.mat-mdc-option, .mat-option'));
        const activeIndex = options.indexOf(activeOption);
        if (activeIndex >= 0) {
          // Access the CdkVirtualScrollViewport instance
          const viewportElement = virtualScrollViewport as any;
          // Try to get the viewport instance from Angular's context
          const viewport = viewportElement._viewport || viewportElement;
          if (viewport && typeof viewport.scrollToIndex === 'function') {
            viewport.scrollToIndex(activeIndex, 'smooth');
          } else {
            // Fallback: calculate scroll position manually
            const itemHeight = 48; // Standard Material option height
            const viewportHeight = virtualScrollViewport.clientHeight;
            const scrollTop = activeIndex * itemHeight - viewportHeight / 2 + itemHeight / 2;
            virtualScrollViewport.scrollTo({
              top: Math.max(0, scrollTop),
              behavior: 'smooth',
            });
          }
        }
      } else {
        // For regular scrolling, use scrollIntoView
        activeOption.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'nearest',
        });
      }
    } catch (error) {
      // Silently fail if there's an error accessing the panel
      console.debug('Error scrolling active option into view:', error);
    }
  }

  findOptionForModelValue(options: SelectOption<T>[], modelValue: T) {
    const comparable =
      modelValue &&
      typeof modelValue === 'object' &&
      'value' in (modelValue as object)
        ? (modelValue as unknown as SelectOption<T>).value
        : modelValue;
    return options.find((option) =>
      this.compareWith(option.value, comparable)
    );
  }

  reinitialize() {
    this.subscription?.unsubscribe();
    this.subscription = this.handleWriteChanges().subscribe();
    this.setupCustomFilter();
  }

  handleWriteChanges() {
    if (!this.options$) {
      throw new Error('Cannot find autocomplete options');
    }
    let lastSyncedOption: SelectOption<T> | undefined;
    return combineLatest([this.options$, this.setModelValue$]).pipe(
      tap(([options, value]) => {
        const option = this.findOptionForModelValue(options, value);
        // Only sync the search text to the selected option's label when the
        // resolved option actually changes (a programmatic writeValue, or options
        // arriving after the value). Without this guard, every re-emission of
        // `options$` — which happens on each keystroke for inline/AG-Grid editors —
        // would overwrite the text the user is currently typing.
        const optionChanged =
          !!option &&
          !(
            lastSyncedOption &&
            this.compareWith(option.value, lastSyncedOption.value)
          );
        if (optionChanged) {
          this.setViewValue$.next(option);
        }
        lastSyncedOption = option;
        super.writeValue(option);
        if (value && !option && options.length > 0) {
          this.setModelValue$.next(null);
        }
      })
    );
  }
  setupCustomFilter() {
    const lowercaseOptions$: Observable<SelectOptionLowercase<T>[]> =
      this.options$.pipe(
        map((options) =>
          options.map((opt) => ({ lowercase: opt.label.toLowerCase(), ...opt }))
        ),
        shareReplay(1)
      );
    const searchTerm$ = this.setViewValue$.pipe(
      map((value) => (typeof value === 'string' ? value.toLowerCase() : '')),
      // If an option is selected, show full list again.
      distinctUntilChanged()
    );
    this.filteredOptions$ = combineLatest([
      searchTerm$,
      lowercaseOptions$,
    ]).pipe(
      map(([searchTerm, allOptions]) => {
        return this.searchMultipleTerms(searchTerm, allOptions);
      }),
      startWith([{ disabled: true, label: 'Loading', value: null }]),
      shareReplay(1)
    );
  }

  searchMultipleTerms(
    searchString: string,
    list: SelectOptionLowercase<T>[]
  ): SelectOptionLowercase<T>[] {
    const searchTerms = searchString.match(/\w+/g);
    if (!searchTerms) {
      return list;
    }
    // Return all items where all search terms can be found in the lowercase version of the label.
    return list.filter((item) => {
      const missingTerm = searchTerms.find(
        (term) => !item.lowercase.includes(term)
      );
      return !missingTerm;
    });
  }
}
